package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.AvailabilityLinkException
import com.evoapps.evoshift.domain.model.AvailabilityLinkException.Reason
import com.evoapps.evoshift.domain.model.AvailabilityLinkForm
import com.evoapps.evoshift.domain.model.AvailabilityPeriod
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.HoursRange
import com.evoapps.evoshift.domain.repository.AvailabilityLinkRepository
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.addJsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject

/**
 * SH-173: the callables behind the e-mailed availability link (functions/availabilityLinks.js),
 * called signed out: availabilityLinkInfo and submitAvailabilityByLink.
 */
class AvailabilityLinkRepositoryImpl(private val functions: CloudFunctionsClient) : AvailabilityLinkRepository {

    override suspend fun form(token: String): Result<AvailabilityLinkForm> = call {
        parseForm(functions.call("availabilityLinkInfo", buildJsonObject { put("token", token) }, requireSignIn = false))
    }

    override suspend fun submit(token: String, days: Map<Int, DayPreference>): Result<Unit> = call {
        functions.call("submitAvailabilityByLink", buildJsonObject {
            put("token", token)
            putJsonObject("days") {
                days.entries.sortedBy { it.key }.forEach { (day, pref) ->
                    putJsonObject(day.toString()) {
                        put("status", pref.status.name)
                        put("prefSlotId", pref.preferredSlotId)
                        put("comment", pref.comment?.trim()?.takeIf { it.isNotEmpty() })
                        putJsonArray("hours") {
                            pref.hours.forEach { h -> addJsonObject { put("start", h.start); put("end", h.end) } }
                        }
                    }
                }
            }
        }, requireSignIn = false)
        Unit
    }

    private suspend fun <T> call(block: suspend () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CloudFunctionsException) {
        Result.failure(refusal(e))
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        /** The functions' refusal as the domain's reason; other failures (offline, internal) stay as they are. */
        fun refusal(e: CloudFunctionsException): Exception {
            val reason = (e.details as? JsonObject)?.get("reason")?.jsonPrimitive?.contentOrNull
            return when {
                e.code == "not-found" || reason == "LINK_INVALID" -> AvailabilityLinkException(Reason.INVALID)
                e.code == "resource-exhausted" -> AvailabilityLinkException(Reason.RATE_LIMITED)
                reason == "LOCKED" -> AvailabilityLinkException(Reason.LOCKED)
                reason == "READ_ONLY" -> AvailabilityLinkException(Reason.READ_ONLY)
                reason == "INVALID_DAYS" || e.code == "invalid-argument" -> AvailabilityLinkException(Reason.INVALID_DAYS)
                else -> e
            }
        }

        /** availabilityLinkInfo's answer. */
        fun parseForm(result: JsonElement): AvailabilityLinkForm {
            val o = result.jsonObject
            fun text(obj: JsonObject, key: String) = obj[key]?.let { runCatching { it.jsonPrimitive.contentOrNull }.getOrNull() }
            val period = (o["period"] as? JsonObject)?.let { p ->
                val start = text(p, "startDate")
                val end = text(p, "endDate")
                if (start != null && end != null) AvailabilityPeriod(start, end) else null
            }
            val slots = (o["slots"]?.jsonArray ?: emptyList()).map { s ->
                val slot = s.jsonObject
                AvailabilitySlot(
                    id = text(slot, "id").orEmpty(),
                    label = text(slot, "label").orEmpty(),
                    startTime = text(slot, "startTime").orEmpty(),
                    endTime = text(slot, "endTime").orEmpty()
                )
            }
            val days = (o["days"] as? JsonObject).orEmpty().mapNotNull { (key, value) ->
                val day = key.toIntOrNull() ?: return@mapNotNull null
                val d = value.jsonObject
                day to DayPreference(
                    status = if (text(d, "status") == AvailabilityStatus.UNAVAILABLE.name) AvailabilityStatus.UNAVAILABLE else AvailabilityStatus.AVAILABLE,
                    preferredSlotId = text(d, "prefSlotId"),
                    comment = text(d, "comment"),
                    hours = (d["hours"]?.jsonArray ?: emptyList()).mapNotNull { h ->
                        val r = h.jsonObject
                        val start = text(r, "start")
                        val end = text(r, "end")
                        if (start != null && end != null) HoursRange(start, end) else null
                    }
                )
            }.toMap()
            return AvailabilityLinkForm(
                firstName = text(o, "firstName").orEmpty(),
                workplaceName = text(o, "workplaceName").orEmpty(),
                month = text(o, "month").orEmpty().ifEmpty { error("The link's form has no month") },
                period = period,
                dueDate = text(o, "dueDate"),
                expiresAt = text(o, "expiresAt").orEmpty(),
                message = text(o, "message")?.takeIf { it.isNotBlank() },
                firstDayOfWeek = o["firstDayOfWeek"]?.jsonPrimitive?.intOrNull?.takeIf { it in 1..7 } ?: 1,
                slots = slots,
                days = days,
                submitted = o["submitted"]?.jsonPrimitive?.booleanOrNull == true,
                locked = o["locked"]?.jsonPrimitive?.booleanOrNull == true
            )
        }
    }
}
